import * as React from 'react';
import { Container, Header, Message, Table, Label, Loader } from 'semantic-ui-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import useCsrfFetchJson from './hooks/use_csrf_fetch';
import OocChat from './ooc_chat';

const CharactersTable = ({ apiPath }) => {
  const fetchJson = useCsrfFetchJson();
  const {
    data = [],
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['player', 'characters'],
    queryFn: () => fetchJson(apiPath),
  });

  if (isLoading) {
    return <Loader active inline="centered">Loading characters...</Loader>;
  }

  if (isError) {
    return (
      <Message negative>
        <Message.Header>Unable to load characters</Message.Header>
        <p>{error.message}</p>
      </Message>
    );
  }

  if (!data.length) {
    return (
      <Message info>
        <Message.Header>No characters found</Message.Header>
        <p>Create a character in the game to see it here.</p>
      </Message>
    );
  }

  return (
    <Table celled striped>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Level</Table.HeaderCell>
          <Table.HeaderCell>Race</Table.HeaderCell>
          <Table.HeaderCell>Class</Table.HeaderCell>
          <Table.HeaderCell>Faction</Table.HeaderCell>
          <Table.HeaderCell>Online</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((character) => (
          <Table.Row key={character.id}>
            <Table.Cell>{character.name}</Table.Cell>
            <Table.Cell>{character.top_level || '-'}</Table.Cell>
            <Table.Cell>{character.race || '-'}</Table.Cell>
            <Table.Cell>{character.main_ability || '-'}</Table.Cell>
            <Table.Cell>{character.current_faction || '-'}</Table.Cell>
            <Table.Cell>
              {character.is_online ? <Label color="green">Online</Label> : <Label>Offline</Label>}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

const PlayerDashboard = ({ api_path }) => {
  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));

  const apiPath = api_path || '/player/characters/mine';
  const oocApiPath = '/player/mud_channels/ooc_messages';

  return (
    <QueryClientProvider client={queryClient}>
      <Container>
        <Header as="h1">Player Dashboard</Header>
        <Header as="h3">Your Characters</Header>
        <CharactersTable apiPath={apiPath} />
        <OocChat apiPath={oocApiPath} />
      </Container>
    </QueryClientProvider>
  );
};

export default PlayerDashboard;
